/**
 * DigiWedge Shared Typography Tokens
 * Consistent typography scale for web and mobile platforms.
 *
 * Usage:
 * - Mobile (React Native): Use numeric values directly in StyleSheet
 * - Web (CSS/Tailwind): Use CSS values or map to Tailwind classes
 */
// =============================================================================
// Font Sizes (pixels for mobile, with CSS equivalents)
// =============================================================================
/**
 * Font size scale in pixels (for React Native)
 */
export const fontSize = {
    /** 10px - Extra small captions */
    xs: 10,
    /** 12px - Small text, labels */
    sm: 12,
    /** 14px - Body text, default */
    md: 14,
    /** 16px - Large body, emphasized */
    lg: 16,
    /** 18px - Subheadings */
    xl: 18,
    /** 20px - Small headings */
    '2xl': 20,
    /** 24px - Medium headings */
    '3xl': 24,
    /** 30px - Large headings */
    '4xl': 30,
    /** 36px - Display headings */
    '5xl': 36,
    /** 48px - Hero headings */
    '6xl': 48,
};
/**
 * Font size scale in rem (for web CSS)
 */
export const fontSizeCss = {
    xs: '0.625rem', // 10px
    sm: '0.75rem', // 12px
    md: '0.875rem', // 14px
    lg: '1rem', // 16px
    xl: '1.125rem', // 18px
    '2xl': '1.25rem', // 20px
    '3xl': '1.5rem', // 24px
    '4xl': '1.875rem', // 30px
    '5xl': '2.25rem', // 36px
    '6xl': '3rem', // 48px
};
// =============================================================================
// Font Weights
// =============================================================================
/**
 * Font weight scale
 * String values for React Native compatibility
 */
export const fontWeight = {
    /** 300 - Light */
    light: '300',
    /** 400 - Normal/Regular */
    normal: '400',
    /** 500 - Medium */
    medium: '500',
    /** 600 - Semibold */
    semibold: '600',
    /** 700 - Bold */
    bold: '700',
    /** 800 - Extra bold */
    extrabold: '800',
};
/**
 * Numeric font weights (for CSS)
 */
export const fontWeightNumeric = {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
};
// =============================================================================
// Line Heights
// =============================================================================
/**
 * Line height scale (unitless multipliers)
 */
export const lineHeight = {
    /** 1.0 - Tight, single line */
    none: 1,
    /** 1.25 - Compact headings */
    tight: 1.25,
    /** 1.375 - Snug paragraphs */
    snug: 1.375,
    /** 1.5 - Normal body text */
    normal: 1.5,
    /** 1.625 - Relaxed reading */
    relaxed: 1.625,
    /** 2.0 - Loose, spacious */
    loose: 2,
};
/**
 * Line height in pixels (for React Native)
 * Calculated based on common font sizes
 */
export const lineHeightPx = {
    xs: 14, // 10px * 1.4
    sm: 18, // 12px * 1.5
    md: 20, // 14px * 1.43
    lg: 24, // 16px * 1.5
    xl: 26, // 18px * 1.44
    '2xl': 28, // 20px * 1.4
    '3xl': 32, // 24px * 1.33
    '4xl': 38, // 30px * 1.27
    '5xl': 44, // 36px * 1.22
    '6xl': 56, // 48px * 1.17
};
// =============================================================================
// Letter Spacing
// =============================================================================
/**
 * Letter spacing scale (in pixels for mobile)
 */
export const letterSpacing = {
    /** -0.5px - Tight */
    tighter: -0.5,
    /** -0.25px - Slightly tight */
    tight: -0.25,
    /** 0 - Normal */
    normal: 0,
    /** 0.25px - Slightly wide */
    wide: 0.25,
    /** 0.5px - Wide */
    wider: 0.5,
    /** 1px - Very wide */
    widest: 1,
};
/**
 * Letter spacing in em (for web CSS)
 */
export const letterSpacingCss = {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
};
// =============================================================================
// Text Styles (Semantic Presets)
// =============================================================================
/**
 * Predefined text styles for common UI patterns
 * Mobile-ready with numeric values
 */
export const textStyles = {
    // Headings
    h1: {
        fontSize: fontSize['5xl'],
        fontWeight: fontWeight.bold,
        lineHeight: lineHeightPx['5xl'],
        letterSpacing: letterSpacing.tight,
    },
    h2: {
        fontSize: fontSize['4xl'],
        fontWeight: fontWeight.bold,
        lineHeight: lineHeightPx['4xl'],
        letterSpacing: letterSpacing.tight,
    },
    h3: {
        fontSize: fontSize['3xl'],
        fontWeight: fontWeight.semibold,
        lineHeight: lineHeightPx['3xl'],
        letterSpacing: letterSpacing.normal,
    },
    h4: {
        fontSize: fontSize['2xl'],
        fontWeight: fontWeight.semibold,
        lineHeight: lineHeightPx['2xl'],
        letterSpacing: letterSpacing.normal,
    },
    h5: {
        fontSize: fontSize.xl,
        fontWeight: fontWeight.medium,
        lineHeight: lineHeightPx.xl,
        letterSpacing: letterSpacing.normal,
    },
    h6: {
        fontSize: fontSize.lg,
        fontWeight: fontWeight.medium,
        lineHeight: lineHeightPx.lg,
        letterSpacing: letterSpacing.normal,
    },
    // Body text
    bodyLarge: {
        fontSize: fontSize.lg,
        fontWeight: fontWeight.normal,
        lineHeight: lineHeightPx.lg,
        letterSpacing: letterSpacing.normal,
    },
    body: {
        fontSize: fontSize.md,
        fontWeight: fontWeight.normal,
        lineHeight: lineHeightPx.md,
        letterSpacing: letterSpacing.normal,
    },
    bodySmall: {
        fontSize: fontSize.sm,
        fontWeight: fontWeight.normal,
        lineHeight: lineHeightPx.sm,
        letterSpacing: letterSpacing.normal,
    },
    // Labels and captions
    label: {
        fontSize: fontSize.sm,
        fontWeight: fontWeight.medium,
        lineHeight: lineHeightPx.sm,
        letterSpacing: letterSpacing.wide,
    },
    caption: {
        fontSize: fontSize.xs,
        fontWeight: fontWeight.normal,
        lineHeight: lineHeightPx.xs,
        letterSpacing: letterSpacing.normal,
    },
    // Interactive elements
    button: {
        fontSize: fontSize.md,
        fontWeight: fontWeight.semibold,
        lineHeight: lineHeightPx.md,
        letterSpacing: letterSpacing.wide,
    },
    buttonSmall: {
        fontSize: fontSize.sm,
        fontWeight: fontWeight.semibold,
        lineHeight: lineHeightPx.sm,
        letterSpacing: letterSpacing.wide,
    },
    link: {
        fontSize: fontSize.md,
        fontWeight: fontWeight.medium,
        lineHeight: lineHeightPx.md,
        letterSpacing: letterSpacing.normal,
    },
    // Data display
    statValue: {
        fontSize: fontSize['3xl'],
        fontWeight: fontWeight.bold,
        lineHeight: lineHeightPx['3xl'],
        letterSpacing: letterSpacing.tight,
    },
    statLabel: {
        fontSize: fontSize.xs,
        fontWeight: fontWeight.medium,
        lineHeight: lineHeightPx.xs,
        letterSpacing: letterSpacing.wide,
    },
};
// =============================================================================
// Flat Export (for backward compatibility)
// =============================================================================
export const typography = {
    fontSize,
    fontSizeCss,
    fontWeight,
    fontWeightNumeric,
    lineHeight,
    lineHeightPx,
    letterSpacing,
    letterSpacingCss,
    textStyles,
};
//# sourceMappingURL=typography.js.map